import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { TS } from "../constants/testIds2";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

function currentMonth() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

export default function Compliance() {
  const { user, tenantId } = useAuth();
  const [month, setMonth] = useState(currentMonth());
  const [rows, setRows] = useState([]);
  const [mine, setMine] = useState(null);
  const [loading, setLoading] = useState(false);
  const isAdmin = ["super_admin", "company_admin", "hr"].includes(user?.role);

  useEffect(() => {
    setLoading(true);
    if (isAdmin) {
      const params = { month };
      if (user?.role === "super_admin" && tenantId) params.tenant_id = tenantId;
      api.get("/compliance/tenant", { params })
        .then(({ data }) => setRows(data))
        .finally(() => setLoading(false));
    } else if (user?.employee_id) {
      api.get("/compliance/mine", { params: { month } })
        .then(({ data }) => setMine(data))
        .finally(() => setLoading(false));
    } else setLoading(false);
  }, [month, tenantId, isAdmin, user?.role, user?.employee_id]);

  const totals = useMemo(() => {
    const t = { expected: 0, actual: 0, deductionDays: 0 };
    for (const r of rows) {
      t.expected += r.expected_hours; t.actual += r.actual_hours; t.deductionDays += r.deduction_days;
    }
    return t;
  }, [rows]);

  return (
    <div className="space-y-6">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-emerald-700">Time</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold tracking-tight">Working-hours compliance</h1>
          <p className="text-sm text-zinc-500 mt-2">Monthly required-hours vs actual · Attendance & timesheet reconciliation.</p>
        </div>
        <div>
          <Label className="text-xs">Month</Label>
          <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="rounded-sm" data-testid="compliance-month-input" />
        </div>
      </div>

      {isAdmin ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <StatBox label="Total expected hours" value={`${totals.expected.toFixed(1)}h`} />
            <StatBox label="Total actual hours" value={`${totals.actual.toFixed(1)}h`} accent={totals.actual < totals.expected ? "orange" : "emerald"} />
            <StatBox label="Estimated deduction days" value={totals.deductionDays.toFixed(2)} accent={totals.deductionDays > 0 ? "orange" : "emerald"} />
          </div>

          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table" data-testid={TS.complianceTable}>
              <thead>
                <tr><th>Employee</th><th>Department</th><th>Working days</th><th>Present</th><th>Expected h</th><th>Actual h</th><th>Attendance h</th><th>Timesheet h</th><th>Variance</th><th>Deduction (days)</th></tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={10} className="text-center py-6 text-zinc-500 text-sm">Loading…</td></tr>}
                {!loading && rows.length === 0 && <tr><td colSpan={10} className="text-center py-8 text-zinc-500 text-sm">No data.</td></tr>}
                {rows.map((r) => (
                  <tr key={r.employee_id}>
                    <td>
                      <div className="text-sm">{r.employee_name}</div>
                      <div className="text-xs font-mono-plex text-zinc-500">{r.employee_code}</div>
                    </td>
                    <td>{r.department || "—"}</td>
                    <td className="font-mono-plex text-xs">{r.expected_working_days}</td>
                    <td className="font-mono-plex text-xs">{r.present_days}</td>
                    <td className="font-mono-plex text-xs">{r.expected_hours}</td>
                    <td className={`font-mono-plex text-xs font-semibold ${r.actual_hours < r.expected_hours ? "text-orange-600" : "text-emerald-700"}`}>{r.actual_hours}</td>
                    <td className="font-mono-plex text-xs text-zinc-500">{r.attendance_hours}</td>
                    <td className="font-mono-plex text-xs text-zinc-500">{r.timesheet_hours}</td>
                    <td className={`font-mono-plex text-xs ${r.variance_hours < 0 ? "text-orange-600" : "text-emerald-700"}`}>{r.variance_hours}</td>
                    <td className="font-mono-plex text-xs">{r.deduction_days}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : mine ? (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <StatBox label="Working days" value={mine.expected_working_days} />
            <StatBox label="Present days" value={mine.present_days} />
            <StatBox label="Expected hours" value={`${mine.expected_hours}h`} />
            <StatBox label="Actual hours" value={`${mine.actual_hours}h`} accent={mine.actual_hours < mine.expected_hours ? "orange" : "emerald"} />
          </div>

          <div className="overflow-x-auto border border-zinc-200 rounded-sm">
            <table className="hr-table">
              <thead><tr><th>Date</th><th>Attendance h</th><th>Timesheet h</th><th>Effective h</th></tr></thead>
              <tbody>
                {mine.daily.map((d) => (
                  <tr key={d.date}>
                    <td className="font-mono-plex text-xs">{d.date}</td>
                    <td className="font-mono-plex text-xs">{d.attendance}</td>
                    <td className="font-mono-plex text-xs">{d.timesheet}</td>
                    <td className={`font-mono-plex text-xs font-semibold ${d.effective < mine.policy.standard_hours_per_day ? "text-orange-600" : "text-emerald-700"}`}>{d.effective}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="text-xs text-zinc-500">
            Policy in effect: standard <b>{mine.policy.standard_hours_per_day}h/day</b>, validation mode <b>{mine.policy.validation_mode}</b>, half-day threshold <b>{mine.policy.half_day_threshold}h</b>.
          </div>
        </>
      ) : (
        <div className="border border-zinc-200 bg-white p-8 rounded-sm text-sm text-zinc-500">No employee profile linked.</div>
      )}
    </div>
  );
}

function StatBox({ label, value, accent }) {
  const cls = accent === "orange" ? "bg-orange-50 text-orange-700 border-orange-200"
    : accent === "emerald" ? "bg-emerald-50 text-emerald-800 border-emerald-200"
    : "bg-white text-zinc-950 border-zinc-200";
  return (
    <div className={`border p-4 rounded-sm ${cls}`}>
      <div className="text-xs uppercase tracking-widest opacity-80">{label}</div>
      <div className="mt-1 font-display text-3xl font-bold">{value}</div>
    </div>
  );
}
